import { useRef, useState } from "react";

// Konami-code easter egg. The clip is self-hosted with the creator's
// permission. The element only mounts once chaos mode fires, so nothing
// is fetched on a normal page load.
const CHAOS_SRC = "/chaos.mp4";

export function ChaosPlayer({ onClose }: { onClose: () => void }) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [muted, setMuted] = useState(true);

  // React doesn't reliably reflect `muted` onto the DOM property, so set it
  // (and kick off playback) imperatively when the element mounts.
  const attachVideo = (el: HTMLVideoElement | null) => {
    videoRef.current = el;
    if (el) {
      el.muted = muted;
      el.play().catch(() => {});
    }
  };

  const toggleMute = () => {
    const next = !muted;
    const el = videoRef.current;
    if (el) {
      el.muted = next;
      if (!next) el.play().catch(() => {});
    }
    setMuted(next);
  };

  const muteLabel = muted ? "Unmute" : "Mute";

  return (
    <div
      style={{
        position: "fixed",
        right: 20,
        bottom: 20,
        zIndex: 85,
        width: "min(300px,calc(100vw - 40px))",
        border: "3px solid var(--fg)",
        background: "var(--bg)",
        boxShadow: "8px 8px 0 var(--accent)",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
          padding: "6px 10px",
          borderBottom: "3px solid var(--fg)",
          fontFamily: "'Archivo Black',sans-serif",
          fontSize: 11,
          letterSpacing: "0.14em",
          color: "var(--fg)",
        }}
      >
        <span>NOW PLAYING</span>
        <div style={{ display: "flex", gap: 6 }}>
          <button
            onClick={toggleMute}
            aria-label={muteLabel}
            title={muteLabel}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "transparent",
              color: "var(--fg)",
              border: "2px solid var(--fg)",
              padding: "4px 6px",
              cursor: "pointer",
            }}
          >
            {muted ? (
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="square"
              >
                <path
                  d="M4 9v6h4l5 4V5L8 9H4z"
                  fill="currentColor"
                  stroke="none"
                />
                <path d="M17 9l5 6M22 9l-5 6" />
              </svg>
            ) : (
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="square"
              >
                <path
                  d="M4 9v6h4l5 4V5L8 9H4z"
                  fill="currentColor"
                  stroke="none"
                />
                <path d="M16.5 8.5a5 5 0 010 7M19.5 6a9 9 0 010 12" />
              </svg>
            )}
          </button>
          <button
            onClick={onClose}
            style={{
              fontFamily: "'Archivo Black',sans-serif",
              fontSize: 12,
              lineHeight: 1,
              background: "var(--accent)",
              color: "var(--bg)",
              border: 0,
              padding: "5px 8px",
              cursor: "pointer",
            }}
          >
            ✕
          </button>
        </div>
      </div>
      <div
        style={{
          position: "relative",
          width: "100%",
          paddingTop: "100%",
          overflow: "hidden",
          background: "#000",
        }}
      >
        <video
          ref={attachVideo}
          src={CHAOS_SRC}
          autoPlay
          loop
          muted
          playsInline
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%,-50%)",
            width: "100%",
            height: "100%",
            objectFit: "cover",
            background: "#000",
          }}
        />
      </div>
    </div>
  );
}
